import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

const MitraCompletedDashboard = () => {
    const [mitras, setMitras] = useState([]);
    const [users, setUsers] = useState({});
    const [summary, setSummary] = useState({});
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Current mitra being viewed
    const [currentMitra, setCurrentMitra] = useState(null);
    
    // Filter & search state
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('total_completed');
    const [sortOrder, setSortOrder] = useState('desc');
    const [monthFilter, setMonthFilter] = useState('');
    const [yearFilter, setYearFilter] = useState('');
    const [viewMode, setViewMode] = useState('filtered');
    const [statusFilter, setStatusFilter] = useState(''); // NEW: Status filter
    const [revenueRange, setRevenueRange] = useState({ min: '', max: '' }); // NEW: Revenue range filter
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12); // NEW: Variable items per page
    
    // Export state
    const [exporting, setExporting] = useState(false); // NEW: Export functionality
    
    // API config
    const API_BASE_URL = 'https://api.tomassage.id/api';

    
    // NEW: Get current date for filters
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    // Fetch data from API
    const fetchMitraData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const offset = (currentPage - 1) * itemsPerPage;
            
            const params = {
                limit: itemsPerPage,
                offset: offset,
                sort_by: sortBy,
                sort_order: sortOrder
            };
            
            if (viewMode === 'filtered') {
                if (monthFilter) {
                    params.month = monthFilter;
                }
                if (yearFilter && !monthFilter) {
                    params.year = yearFilter;
                }
            }
            
            // NEW: Additional filter parameters
            if (statusFilter) {
                params.status = statusFilter;
            }
            if (revenueRange.min) {
                params.min_revenue = revenueRange.min;
            }
            if (revenueRange.max) {
                params.max_revenue = revenueRange.max;
            }
            
            console.log('📡 Fetching mitras with params:', params);
            
            const response = await axios.get(`${API_BASE_URL}/completed-mitra`, { params });
            
            if (response.data.success) {
                setMitras(response.data.data || []);
                setUsers(response.data.users || {});
                setSummary(response.data.summary || {});
                setPagination(response.data.pagination || {});
            } else {
                throw new Error(response.data.message || 'Failed to fetch data');
            }
            
        } catch (err) {
            console.error('Error fetching mitra data:', err);
            setError('Gagal memuat data: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };
    
    // NEW: Export data to CSV
    const exportToCSV = async () => {
        try {
            setExporting(true);
            const response = await axios.get(`${API_BASE_URL}/completed-mitra/export`, {
                params: {
                    format: 'csv',
                    view_mode: viewMode,
                    month: monthFilter,
                    year: yearFilter,
                    status: statusFilter,
                    min_revenue: revenueRange.min,
                    max_revenue: revenueRange.max
                },
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `mitra-data-${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setError('Gagal mengexport data: ' + err.message);
        } finally {
            setExporting(false);
        }
    };
    
    // NEW: Generate month options for current year
    const getMonthOptions = () => {
        const months = [];
        for (let i = 1; i <= 12; i++) {
            const monthStr = i.toString().padStart(2, '0');
            const monthName = new Date(currentYear, i - 1).toLocaleDateString('id-ID', { month: 'long' });
            months.push({
                value: `${currentYear}-${monthStr}`,
                label: `${monthName} ${currentYear}`
            });
        }
        return months;
    };
    
    // NEW: Generate year options
    const getYearOptions = () => {
        const years = [];
        for (let i = currentYear; i >= currentYear - 5; i--) {
            years.push({ value: i.toString(), label: i.toString() });
        }
        return years;
    };
    
    useEffect(() => {
        fetchMitraData();
    }, [currentPage, sortBy, sortOrder, monthFilter, yearFilter, viewMode, statusFilter, revenueRange, itemsPerPage]);
    
    // Format price
    const formatPrice = (price) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price || 0);
    };
    
    // Format compact price (K, M, B)
    const formatCompactPrice = (price) => {
        const numPrice = parseFloat(price) || 0;
        if (numPrice >= 1000000000) {
            return (numPrice / 1000000000).toFixed(1) + 'M';
        } else if (numPrice >= 1000000) {
            return (numPrice / 1000000).toFixed(1) + 'Jt';
        } else if (numPrice >= 1000) {
            return (numPrice / 1000).toFixed(0) + 'K';
        }
        return formatPrice(numPrice);
    };
    
    // Filter mitras by search term
    const filteredMitras = mitras.filter(mitra => {
        if (!searchTerm) return true;
        const name = mitra.mitra_data?.nama_lengkap?.toLowerCase() || '';
        const phone = mitra.mitra_data?.nomor_wa?.toLowerCase() || '';
        const search = searchTerm.toLowerCase();
        return name.includes(search) || phone.includes(search);
    });
    
    // Reset filters
    const resetFilters = () => {
        setSearchTerm('');
        setSortBy('total_completed');
        setSortOrder('desc');
        setMonthFilter('');
        setYearFilter('');
        setViewMode('filtered');
        setStatusFilter('');
        setRevenueRange({ min: '', max: '' });
        setCurrentPage(1);
        setItemsPerPage(12);
    };
    
    // Get month display text
    const getMonthDisplayText = () => {
        if (monthFilter) {
            try {
                const date = new Date(monthFilter + '-01');
                return date.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
            } catch (e) {
                return monthFilter;
            }
        }
        if (yearFilter) {
            return yearFilter;
        }
        return 'Semua Periode';
    };
    
    // Calculate display statistics based on view mode
    const getDisplayStats = (mitra) => {
        return viewMode === 'alltime' ? {
            completed: mitra.all_time_completed || 0,
            revenue: mitra.all_time_revenue || 0,
            customers: mitra.all_time_unique_customers || 0,
            avgTransaction: mitra.all_time_avg_transaction_value || 0,
            bookings: mitra.all_time_bookings || []
        } : {
            completed: mitra.total_completed || 0,
            revenue: mitra.total_revenue || 0,
            customers: mitra.unique_customers || 0,
            avgTransaction: mitra.avg_transaction_value || 0,
            bookings: mitra.completed_bookings || []
        };
    };
    
    // NEW: Performance indicator
    const getPerformanceIndicator = (mitra) => {
        const stats = getDisplayStats(mitra);
        if (stats.completed >= 50 && stats.revenue >= 5000000) {
            return { level: 'Excellent', color: 'success', icon: 'mdi:star-circle' };
        } else if (stats.completed >= 20 && stats.revenue >= 2000000) {
            return { level: 'Good', color: 'primary', icon: 'mdi:thumb-up' };
        } else if (stats.completed >= 10 && stats.revenue >= 1000000) {
            return { level: 'Average', color: 'warning', icon: 'mdi:circle' };
        }
        return { level: 'Below Average', color: 'secondary', icon: 'mdi:trending-down' };
    };
    
    if (loading) {
        return (
            <div className="min-vh-100 d-flex justify-content-center align-items-center bg-gradient-primary">
                <div className="text-center text-white">
                    <div className="spinner-border mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5>Memuat data mitra...</h5>
                </div>
            </div>
        );
    }
    
    return (
        <div className="min-vh-100 bg-light">
            <style jsx>{`
                .bg-gradient-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }
                .bg-gradient-success {
                    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
                }
                .hero-section {
                    position: relative;
                    overflow: hidden;
                }
                .hero-icon {
                    animation: float 6s ease-in-out infinite;
                }
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }
                .mitra-card {
                    transition: all 0.3s ease;
                    border-radius: 1rem;
                    overflow: hidden;
                    position: relative;
                }
                .mitra-card:hover {
                    transform: translateY(-10px) scale(1.02);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15) !important;
                }
                .shadow-hover {
                    box-shadow: 0 4px 6px rgba(0,0,0,0.07);
                }
                .rank-badge {
                    font-size: 0.8rem;
                    padding: 0.4rem 0.7rem;
                    border-radius: 20px;
                    font-weight: 600;
                }
                .rank-top {
                    background: linear-gradient(45deg, #ffd700, #ffed4e);
                    color: #854d0e;
                    animation: pulse 2s infinite;
                }
                .rank-normal {
                    background: rgba(108, 117, 125, 0.1);
                    color: #6c757d;
                }
                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }
                .mitra-avatar {
                    position: relative;
                    width: 80px;
                    height: 80px;
                }
                .avatar-circle {
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%);
                    border: 3px solid rgba(255,255,255,0.3);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.8rem;
                    font-weight: bold;
                    color: white;
                    backdrop-filter: blur(10px);
                }
                .avatar-status {
                    position: absolute;
                    bottom: 0;
                    right: 0;
                    background: #28a745;
                    border-radius: 50%;
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    border: 3px solid white;
                }
                .stat-box {
                    padding: 1rem;
                    border-radius: 0.75rem;
                    text-align: center;
                    position: relative;
                    overflow: hidden;
                    transition: all 0.3s ease;
                }
                .stat-box:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
                }
                .stat-icon {
                    font-size: 1.5rem;
                    margin-bottom: 0.5rem;
                    display: block;
                }
                .stat-value {
                    font-size: 1.4rem;
                    font-weight: 700;
                    line-height: 1.2;
                    margin-bottom: 0.25rem;
                }
                .stat-label {
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    font-weight: 500;
                    opacity: 0.8;
                }
                .fab-container {
                    position: fixed;
                    bottom: 2rem;
                    right: 2rem;
                    z-index: 1000;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }
                .fab {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    border: none;
                    box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
                    transition: all 0.3s ease;
                    background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
                }
                .fab:hover {
                    transform: scale(1.1);
                    box-shadow: 0 12px 35px rgba(40, 167, 69, 0.6);
                }
                .fab-secondary {
                    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
                    box-shadow: 0 8px 25px rgba(0, 123, 255, 0.4);
                }
                .fab-secondary:hover {
                    box-shadow: 0 12px 35px rgba(0, 123, 255, 0.6);
                }
                .pagination-rounded .page-link {
                    border-radius: 50px;
                    margin: 0 0.25rem;
                    border: 2px solid #e9ecef;
                    color: #6c757d;
                    transition: all 0.3s ease;
                }
                .pagination-rounded .page-item.active .page-link {
                    background: linear-gradient(135deg, #007bff, #0056b3);
                    border-color: #007bff;
                    transform: scale(1.1);
                    box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
                }
                .pagination-rounded .page-link:hover {
                    background-color: #f8f9fa;
                    border-color: #007bff;
                    color: #007bff;
                    transform: translateY(-2px);
                }
                .empty-state {
                    padding: 3rem 1rem;
                    animation: fadeInUp 0.6s ease;
                }
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .performance-badge {
                    font-size: 0.7rem;
                    padding: 0.2rem 0.5rem;
                    border-radius: 12px;
                    font-weight: 600;
                }
                .filter-panel {
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                    border-radius: 1rem;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                }
                @media (max-width: 768px) {
                    .hero-section h1 {
                        font-size: 1.5rem;
                    }
                    .mitra-card {
                        margin-bottom: 1rem;
                    }
                    .stat-value {
                        font-size: 1.2rem;
                    }
                    .fab-container {
                        bottom: 1rem;
                        right: 1rem;
                    }
                    .fab {
                        width: 50px;
                        height: 50px;
                    }
                }
            `}</style>
            
            {/* Hero Section */}
            <div className="hero-section bg-gradient-success text-white py-5">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-8">
                            <div className="d-flex align-items-center mb-3">
                                <div className="hero-icon bg-white bg-opacity-20 rounded-circle p-3 me-3">
                                    <Icon icon="mdi:account-star" style={{ fontSize: '2.5rem' }} />
                                </div>
                                <div>
                                    <h1 className="mb-0 fw-bold">Dashboard Mitra Completed</h1>
                                    <p className="mb-0 opacity-90">Performa & statistik mitra dengan transaksi selesai</p>
                                </div>
                            </div>
                            
                            {/* Quick Stats */}
                            {/* <div className="row g-3 mt-3">
                                <div className="col-md-3">
                                    <div className="bg-white bg-opacity-15 rounded-3 p-3 text-center">
                                        <Icon icon="mdi:account-group" className="mb-2" style={{ fontSize: '2rem' }} />
                                        <div className="fw-bold fs-4">{summary.total_mitras || 0}</div>
                                        <small>Mitra Aktif</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="bg-white bg-opacity-15 rounded-3 p-3 text-center">
                                        <Icon icon="mdi:check-circle" className="mb-2" style={{ fontSize: '2rem' }} />
                                        <div className="fw-bold fs-4">
                                            {(viewMode === 'alltime' ? summary.all_time_total_transactions : summary.total_transactions)?.toLocaleString('id-ID') || 0}
                                        </div>
                                        <small>Transaksi Selesai</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="bg-white bg-opacity-15 rounded-3 p-3 text-center">
                                        <Icon icon="mdi:currency-usd" className="mb-2" style={{ fontSize: '2rem' }} />
                                        <div className="fw-bold fs-4">
                                            {formatCompactPrice(viewMode === 'alltime' ? summary.all_time_total_revenue : summary.total_revenue)}
                                        </div>
                                        <small>Total Pendapatan</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="bg-white bg-opacity-15 rounded-3 p-3 text-center">
                                        <Icon icon="mdi:trending-up" className="mb-2" style={{ fontSize: '2rem' }} />
                                        <div className="fw-bold fs-4">{summary.avg_transactions_per_mitra || '0'}</div>
                                        <small>Rata-rata/Mitra</small>
                                    </div>
                                </div>
                            </div> */}
                        </div>
                        <div className="col-lg-4 text-center">
                            <div className="hero-illustration">
                                <Icon icon="mdi:chart-line" style={{ fontSize: '8rem' }} className="opacity-30" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Main Content */}
            <div className="container py-5">
                {/* Error Message */}
                {error && (
                    <div className="alert alert-danger alert-dismissible fade show shadow-sm" role="alert">
                        <Icon icon="mdi:alert-circle" className="me-2" />
                        {error}
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={() => setError(null)}
                        ></button>
                    </div>
                )}
                
                {/* Enhanced Control Panel */}
                <div className="card shadow-lg border-0 mb-4 filter-panel">
                    <div className="card-body p-4">
                        {/* First Row - Main Controls */}
                        <div className="row g-3 align-items-end mb-3">
                            {/* View Mode Toggle */}
                            <div className="col-lg-2 col-md-3">
                                <label className="form-label fw-semibold text-primary">
                                    <Icon icon="mdi:view-dashboard" className="me-1" />
                                    Tampilan Data
                                </label>
                                <div className="btn-group w-100" role="group">
                                    <input 
                                        type="radio" 
                                        className="btn-check" 
                                        name="viewMode" 
                                        id="filtered" 
                                        value="filtered"
                                        checked={viewMode === 'filtered'}
                                        onChange={(e) => {
                                            setViewMode(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                    <label className="btn btn-outline-primary btn-sm" htmlFor="filtered">
                                        Filter
                                    </label>
                                    
                                    <input 
                                        type="radio" 
                                        className="btn-check" 
                                        name="viewMode" 
                                        id="alltime" 
                                        value="alltime"
                                        checked={viewMode === 'alltime'}
                                        onChange={(e) => {
                                            setViewMode(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                    <label className="btn btn-outline-success btn-sm" htmlFor="alltime">
                                        All Time
                                    </label>
                                </div>
                            </div>
                            
                            {/* Search */}
                            <div className="col-lg-3 col-md-4">
                                <label className="form-label fw-semibold text-primary">
                                    <Icon icon="mdi:magnify" className="me-1" />
                                    Pencarian
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Icon icon="mdi:magnify" />
                                    </span>
                                    <input 
                                        type="text" 
                                        className="form-control border-start-0" 
                                        placeholder="Nama mitra atau nomor WA..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            
                            {/* Sort By */}
                            <div className="col-lg-2 col-md-3">
                                <label className="form-label fw-semibold text-primary">
                                    <Icon icon="mdi:sort" className="me-1" />
                                    Urutkan
                                </label>
                                <select 
                                    className="form-select"
                                    value={sortBy}
                                    onChange={(e) => {
                                        setSortBy(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="total_completed">📊 Transaksi</option>
                                    <option value="total_revenue">💰 Pendapatan</option>
                                    <option value="all_time_completed">🏆 All-Time Transaksi</option>
                                    <option value="all_time_revenue">💎 All-Time Pendapatan</option>
                                    <option value="name">📝 Nama</option>
                                </select>
                            </div>
                            
                            {/* Sort Order */}
                            <div className="col-lg-2 col-md-2">
                                <label className="form-label fw-semibold text-primary">
                                    <Icon icon="mdi:swap-vertical" className="me-1" />
                                    Urutan
                                </label>
                                <select 
                                    className="form-select"
                                    value={sortOrder}
                                    onChange={(e) => {
                                        setSortOrder(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="desc">⬇️ Tinggi-Rendah</option>
                                    <option value="asc">⬆️ Rendah-Tinggi</option>
                                </select>
                            </div>
                            
                            {/* Items per page */}
                            <div className="col-lg-1 col-md-2">
                                <label className="form-label fw-semibold text-primary">
                                    <Icon icon="mdi:view-grid" className="me-1" />
                                    Per Halaman
                                </label>
                                <select 
                                    className="form-select"
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(parseInt(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="6">6</option>
                                    <option value="12">12</option>
                                    <option value="24">24</option>
                                    <option value="48">48</option>
                                </select>
                            </div>
                        </div>
                        
                        {/* Second Row - Advanced Filters */}
                        <div className="row g-3 align-items-end">
                            {/* Month Filter */}
                            {viewMode === 'filtered' && (
                                <div className="col-lg-2 col-md-3">
                                    <label className="form-label fw-semibold text-success">
                                        <Icon icon="mdi:calendar-month" className="me-1" />
                                        Bulan
                                    </label>
                                    <select 
                                        className="form-select"
                                        value={monthFilter}
                                        onChange={(e) => {
                                            setMonthFilter(e.target.value);
                                            if (e.target.value) setYearFilter('');
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="">Semua Bulan</option>
                                        {getMonthOptions().map(month => (
                                            <option key={month.value} value={month.value}>
                                                {month.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            {/* Year Filter */}
                            {viewMode === 'filtered' && (
                                <div className="col-lg-2 col-md-3">
                                    <label className="form-label fw-semibold text-success">
                                        <Icon icon="mdi:calendar" className="me-1" />
                                        Tahun
                                    </label>
                                    <select 
                                        className="form-select"
                                        value={yearFilter}
                                        onChange={(e) => {
                                            setYearFilter(e.target.value);
                                            if (e.target.value) setMonthFilter('');
                                            setCurrentPage(1);
                                        }}
                                        disabled={!!monthFilter}
                                    >
                                        <option value="">Semua Tahun</option>
                                        {getYearOptions().map(year => (
                                            <option key={year.value} value={year.value}>
                                                {year.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            {/* Status Filter */}
                            <div className="col-lg-2 col-md-3">
                                <label className="form-label fw-semibold text-info">
                                    <Icon icon="mdi:account-check" className="me-1" />
                                    Status
                                </label>
                                <select 
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="">Semua Status</option>
                                    <option value="aktif">Aktif</option>
                                    <option value="non-aktif">Non-Aktif</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>
                            
                            {/* Revenue Range - Min */}
                            <div className="col-lg-2 col-md-3">
                                <label className="form-label fw-semibold text-warning">
                                    <Icon icon="mdi:currency-usd" className="me-1" />
                                    Min Revenue
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">Rp</span>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        placeholder="0"
                                        value={revenueRange.min}
                                        onChange={(e) => {
                                            setRevenueRange(prev => ({ ...prev, min: e.target.value }));
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            </div>
                            
                            {/* Revenue Range - Max */}
                            <div className="col-lg-2 col-md-3">
                                <label className="form-label fw-semibold text-warning">
                                    <Icon icon="mdi:currency-usd" className="me-1" />
                                    Max Revenue
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">Rp</span>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        placeholder="Unlimited"
                                        value={revenueRange.max}
                                        onChange={(e) => {
                                            setRevenueRange(prev => ({ ...prev, max: e.target.value }));
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="col-lg-2 col-md-3">
                                <div className="d-flex gap-2">
                                    <button 
                                        className="btn btn-outline-danger flex-grow-1" 
                                        onClick={resetFilters}
                                        title="Reset Semua Filter"
                                    >
                                        <Icon icon="mdi:refresh" />
                                    </button>
                                    <button 
                                        className="btn btn-outline-success flex-grow-1" 
                                        onClick={exportToCSV}
                                        disabled={exporting}
                                        title="Export ke CSV"
                                    >
                                        {exporting ? (
                                            <div className="spinner-border spinner-border-sm" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        ) : (
                                            <Icon icon="mdi:download" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Status Info */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-info border-0 shadow-sm">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <Icon icon={viewMode === 'alltime' ? "mdi:infinity" : "mdi:filter"} className="me-2 fs-4" />
                                    <div>
                                        <strong>
                                            {viewMode === 'alltime' ? '🏆 Tampilan All-Time' : '📊 Tampilan Terfilter'} • 
                                            Menampilkan {filteredMitras.length} mitra
                                        </strong>
                                        <div className="small">
                                            📅 {summary.period_display || getMonthDisplayText()} • 
                                            {(viewMode === 'alltime' ? summary.all_time_total_transactions : summary.total_transactions)?.toLocaleString('id-ID') || 0} transaksi • 
                                            {formatPrice(viewMode === 'alltime' ? summary.all_time_total_revenue : summary.total_revenue)}
                                            {searchTerm && (
                                                <> • 🔍 Pencarian: "{searchTerm}"</>
                                            )}
                                            {statusFilter && (
                                                <> • 📊 Status: {statusFilter}</>
                                            )}
                                            {(revenueRange.min || revenueRange.max) && (
                                                <> • 💰 Revenue: {revenueRange.min ? formatPrice(revenueRange.min) : '0'} - {revenueRange.max ? formatPrice(revenueRange.max) : '∞'}</>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {(searchTerm || monthFilter || yearFilter || statusFilter || revenueRange.min || revenueRange.max || viewMode !== 'filtered') && (
                                    <button 
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={resetFilters}
                                    >
                                        <Icon icon="mdi:filter-remove" className="me-1" />
                                        Reset
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Mitra Cards */}
                {filteredMitras.length > 0 ? (
                    <div className="row g-4 mb-5">
                        {filteredMitras.map((mitra, index) => {
                            const globalRank = index + 1 + ((pagination.current_page || 1) - 1) * itemsPerPage;
                            const displayStats = getDisplayStats(mitra);
                            const performance = getPerformanceIndicator(mitra);
                            
                            return (
                                <div key={mitra.id} className="col-xxl-3 col-xl-4 col-lg-6 col-md-6">
                                    <div className="card mitra-card h-100 border-0 shadow-hover">
                                        {/* Rank Badge */}
                                        <div className="position-absolute top-0 start-0 mt-3 ms-3">
                                            <span className={`badge rank-badge ${globalRank <= 3 ? 'rank-top' : 'rank-normal'}`}>
                                                {globalRank <= 3 && (
                                                    <Icon icon={globalRank === 1 ? "mdi:trophy" : globalRank === 2 ? "mdi:medal-outline" : "mdi:star"} className="me-1" />
                                                )}
                                                #{globalRank}
                                            </span>
                                        </div>
                                        
                                        {/* Performance Badge */}
                                        <div className="position-absolute top-0 end-0 mt-3 me-3">
                                            <span className={`badge performance-badge bg-${performance.color} text-white`}>
                                                <Icon icon={performance.icon} className="me-1" />
                                                {performance.level}
                                            </span>
                                        </div>
                                        
                                        {/* Card Header */}
                                        <div className="card-header bg-gradient-success text-white border-0 pb-0">
                                            <div className="text-center pt-3">
                                                <div className="mitra-avatar mx-auto mb-3">
                                                    <div className="avatar-circle">
                                                        {mitra.mitra_data?.nama_lengkap?.charAt(0)?.toUpperCase() || 'M'}
                                                    </div>
                                                    <div className="avatar-status">
                                                        <Icon icon="mdi:check-circle" />
                                                    </div>
                                                </div>
                                                <h6 className="card-title mb-1 fw-bold">
                                                    {mitra.mitra_data?.nama_lengkap || 'Nama tidak tersedia'}
                                                </h6>
                                                <div className="d-flex justify-content-center align-items-center gap-2">
                                                    <span className="badge bg-white bg-opacity-20">
                                                        <Icon icon="mdi:account-check" className="me-1" />
                                                        {mitra.mitra_data?.status || 'Aktif'}
                                                    </span>
                                                    {mitra.mitra_data?.nomor_wa && (
                                                        <span className="badge bg-white bg-opacity-20">
                                                            <Icon icon="mdi:whatsapp" className="me-1" />
                                                            WA
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Card Body */}
                                        <div className="card-body">
                                            {/* Main Stats */}
                                            <div className="row g-3 mb-3">
                                                <div className="col-6">
                                                    <div className="stat-box bg-success bg-opacity-10 border border-success border-opacity-20">
                                                        <Icon icon="mdi:check-circle" className="stat-icon text-success" />
                                                        <div className="stat-value text-success">{displayStats.completed}</div>
                                                        <div className="stat-label">Transaksi</div>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="stat-box bg-warning bg-opacity-10 border border-warning border-opacity-20">
                                                        <Icon icon="mdi:currency-usd" className="stat-icon text-warning" />
                                                        <div className="stat-value text-warning">{formatCompactPrice(displayStats.revenue)}</div>
                                                        <div className="stat-label">Pendapatan</div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Secondary Stats */}
                                            <div className="row g-2 mb-3">
                                                <div className="col-12">
                                                    <div className="d-flex justify-content-between align-items-center bg-light rounded p-2">
                                                        <div className="d-flex align-items-center">
                                                            <Icon icon="mdi:account-group" className="text-info me-2" />
                                                            <small className="text-muted">Customer Unik</small>
                                                        </div>
                                                        <span className="fw-bold text-info">{displayStats.customers}</span>
                                                    </div>
                                                </div>
                                                <div className="col-12">
                                                    <div className="d-flex justify-content-between align-items-center bg-light rounded p-2">
                                                        <div className="d-flex align-items-center">
                                                            <Icon icon="mdi:calculator" className="text-primary me-2" />
                                                            <small className="text-muted">Avg/Transaksi</small>
                                                        </div>
                                                        <span className="fw-bold text-primary">{formatCompactPrice(displayStats.avgTransaction)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Performance Indicator */}
                                            <div className="row g-2">
                                                <div className="col-12">
                                                    <div className={`d-flex justify-content-between align-items-center bg-${performance.color} bg-opacity-10 rounded p-2`}>
                                                        <div className="d-flex align-items-center">
                                                            <Icon icon={performance.icon} className={`text-${performance.color} me-2`} />
                                                            <small className="text-muted">Performance</small>
                                                        </div>
                                                        <span className={`fw-bold text-${performance.color}`}>{performance.level}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Card Footer */}
                                        <div className="card-footer bg-transparent border-0 pt-0">
                                            <div className="d-grid gap-2">
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => setCurrentMitra(mitra)}
                                                >
                                                    <Icon icon="mdi:eye" className="me-1" />
                                                    Lihat Detail
                                                </button>
                                                
                                                {mitra.mitra_data?.nomor_wa && (
                                                    <a 
                                                        href={`https://wa.me/${mitra.mitra_data.nomor_wa.replace(/[^0-9]/g, '')}`}
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="btn btn-success btn-sm"
                                                    >
                                                        <Icon icon="mdi:whatsapp" className="me-1" />
                                                        Hubungi
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="empty-state">
                            <Icon icon="mdi:account-search" style={{ fontSize: '5rem' }} className="text-muted mb-3" />
                            <h4 className="text-muted">Tidak ada mitra yang ditemukan</h4>
                            <p className="text-muted">
                                {searchTerm || monthFilter || yearFilter || statusFilter || revenueRange.min || revenueRange.max ? 
                                    'Coba ubah filter pencarian Anda' : 
                                    'Belum ada mitra dengan transaksi selesai'}
                            </p>
                            {(searchTerm || monthFilter || yearFilter || statusFilter || revenueRange.min || revenueRange.max) && (
                                <button 
                                    className="btn btn-primary mt-3"
                                    onClick={resetFilters}
                                >
                                    <Icon icon="mdi:filter-remove" className="me-1" />
                                    Reset Semua Filter
                                </button>
                            )}
                        </div>
                    </div>
                )}
                
                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-center">
                        <nav aria-label="Page navigation">
                            <ul className="pagination pagination-rounded">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        <Icon icon="mdi:chevron-left" />
                                    </button>
                                </li>
                                
                                {/* Page Numbers */}
                                {(() => {
                                    const pages = [];
                                    const maxVisible = 5;
                                    const totalPages = pagination.total_pages || 1;
                                    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                                    
                                    if (endPage - startPage < maxVisible - 1) {
                                        startPage = Math.max(1, endPage - maxVisible + 1);
                                    }
                                    
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => setCurrentPage(i)}>
                                                    {i}
                                                </button>
                                            </li>
                                        );
                                    }
                                    return pages;
                                })()}
                                
                                <li className={`page-item ${currentPage === pagination.total_pages ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={currentPage === pagination.total_pages}
                                    >
                                        <Icon icon="mdi:chevron-right" />
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
                
                {/* Counter */}
                {filteredMitras.length > 0 && (
                    <div className="text-center mt-4">
                        <small className="text-muted">
                            Halaman {pagination.current_page || 1} dari {pagination.total_pages || 1} • 
                            Menampilkan {filteredMitras.length} dari {pagination.total_items || 0} mitra
                            {viewMode === 'alltime' && (
                                <span className="ms-2 badge bg-success">🏆 All-Time Data</span>
                            )}
                        </small>
                    </div>
                )}
            </div>
            
            {/* Enhanced Floating Action Buttons */}
            <div className="fab-container">
                <button
                    className="fab bg-success text-white"
                    onClick={fetchMitraData}
                    title="Refresh Data"
                >
                    <Icon icon="mdi:refresh" style={{ fontSize: '1.5rem' }} />
                </button>
                
                <button
                    className="fab fab-secondary text-white"
                    onClick={exportToCSV}
                    disabled={exporting}
                    title="Export ke CSV"
                >
                    {exporting ? (
                        <div className="spinner-border spinner-border-sm text-white" role="status">
                            <span className="visually-hidden">Exporting...</span>
                        </div>
                    ) : (
                        <Icon icon="mdi:download" style={{ fontSize: '1.5rem' }} />
                    )}
                </button>
                
                <button
                    className="fab bg-warning text-white"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    title="Ke Atas"
                >
                    <Icon icon="mdi:chevron-up" style={{ fontSize: '1.5rem' }} />
                </button>
            </div>
            
            {/* Enhanced Modal for Mitra Detail */}
            {currentMitra && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl modal-dialog-scrollable">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-gradient-success text-white border-0">
                                <h5 className="modal-title d-flex align-items-center">
                                    <Icon icon="mdi:account-details" className="me-2" />
                                    Detail Mitra - {currentMitra.mitra_data?.nama_lengkap}
                                    <span className={`badge bg-${getPerformanceIndicator(currentMitra).color} ms-2`}>
                                        {getPerformanceIndicator(currentMitra).level}
                                    </span>
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setCurrentMitra(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="container-fluid">
                                    {/* Mitra Basic Info */}
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <h6 className="card-title mb-3">
                                                        <Icon icon="mdi:account-details" className="me-2 text-primary" />
                                                        Informasi Mitra
                                                    </h6>
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <strong>Nama Lengkap:</strong><br/>
                                                            <span className="text-muted">{currentMitra.mitra_data?.nama_lengkap || '-'}</span>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <strong>Nomor WhatsApp:</strong><br/>
                                                            <span className="text-muted">{currentMitra.mitra_data?.nomor_wa || '-'}</span>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <strong>Status:</strong><br/>
                                                            <span className={`badge bg-${currentMitra.mitra_data?.status === 'aktif' ? 'success' : 'secondary'}`}>
                                                                {currentMitra.mitra_data?.status || 'Tidak Diketahui'}
                                                            </span>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <strong>Join Date:</strong><br/>
                                                            <span className="text-muted">{currentMitra.mitra_data?.created_at ? new Date(currentMitra.mitra_data.created_at).toLocaleDateString('id-ID') : '-'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Mitra Stats Comparison */}
                                    <div className="row g-4 mb-4">
                                        <div className="col-lg-3 col-md-6">
                                            <div className="card bg-primary text-white h-100">
                                                <div className="card-body text-center">
                                                    <Icon icon="mdi:infinity" style={{ fontSize: '3rem' }} className="mb-2" />
                                                    <h4>{currentMitra.all_time_completed || 0}</h4>
                                                    <p className="mb-0">Total Transaksi All-Time</p>
                                                    <small className="opacity-75">
                                                        vs {currentMitra.total_completed || 0} periode ini
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-3 col-md-6">
                                            <div className="card bg-success text-white h-100">
                                                <div className="card-body text-center">
                                                    <Icon icon="mdi:currency-usd" style={{ fontSize: '3rem' }} className="mb-2" />
                                                    <h4>{formatCompactPrice(currentMitra.all_time_revenue)}</h4>
                                                    <p className="mb-0">Pendapatan All-Time</p>
                                                    <small className="opacity-75">
                                                        vs {formatCompactPrice(currentMitra.total_revenue)} periode ini
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-3 col-md-6">
                                            <div className="card bg-warning text-white h-100">
                                                <div className="card-body text-center">
                                                    <Icon icon="mdi:account-heart" style={{ fontSize: '3rem' }} className="mb-2" />
                                                    <h4>{currentMitra.all_time_unique_customers || 0}</h4>
                                                    <p className="mb-0">Customer Unik All-Time</p>
                                                    <small className="opacity-75">
                                                        vs {currentMitra.unique_customers || 0} periode ini
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-3 col-md-6">
                                            <div className="card bg-info text-white h-100">
                                                <div className="card-body text-center">
                                                    <Icon icon="mdi:calculator" style={{ fontSize: '3rem' }} className="mb-2" />
                                                    <h4>{formatPrice(currentMitra.all_time_avg_transaction_value || 0)}</h4>
                                                    <p className="mb-0">Rata-rata per Transaksi</p>
                                                    <small className="opacity-75">
                                                        vs {formatPrice(currentMitra.avg_transaction_value || 0)} periode ini
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Transaction History */}
                                    <div className="card">
                                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0 d-flex align-items-center">
                                                <Icon icon="mdi:history" className="me-2 text-primary" />
                                                Riwayat Transaksi Terbaru ({(currentMitra.all_time_bookings || []).length})
                                            </h6>
                                            <div className="d-flex gap-2">
                                                <span className="badge bg-success">All Completed</span>
                                                <button 
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => {
                                                        // Implement export single mitra functionality
                                                        console.log('Export single mitra data');
                                                    }}
                                                >
                                                    <Icon icon="mdi:download" className="me-1" />
                                                    Export
                                                </button>
                                            </div>
                                        </div>
                                        <div className="card-body p-0">
                                            {currentMitra.all_time_bookings && currentMitra.all_time_bookings.length > 0 ? (
                                                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                                    <table className="table table-hover mb-0">
                                                        <thead className="table-success sticky-top">
                                                            <tr>
                                                                <th width="5%">#</th>
                                                                <th width="15%">Tanggal</th>
                                                                <th width="25%">Customer</th>
                                                                <th width="30%">Layanan</th>
                                                                <th width="15%">Harga</th>
                                                                <th width="10%">Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentMitra.all_time_bookings.slice(0, 50).map((booking, index) => (
                                                                <tr key={booking.id || index}>
                                                                    <td>
                                                                        <span className="badge bg-light text-dark">{index + 1}</span>
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex flex-column">
                                                                            <span className="fw-semibold">{booking.tanggal_booking ? new Date(booking.tanggal_booking).toLocaleDateString('id-ID') : '-'}</span>
                                                                            <small className="text-muted">#{booking.id || '-'}</small>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div>
                                                                            <div className="fw-semibold">{users[booking.id_user]?.nama_lengkap || `User ID ${booking.id_user}`}</div>
                                                                            <small className="text-muted">{booking.gender_select || '-'}</small>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div>
                                                                            <div className="fw-semibold text-primary">{booking.nama_service || '-'}</div>
                                                                            <small className="text-muted">{booking.varian_service || '-'}</small>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <span className="fw-bold text-success">{formatPrice(booking.harga_service)}</span>
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex align-items-center">
                                                                            <Icon icon="mdi:check-circle" className="text-success me-1" />
                                                                            <small className="text-success">Selesai</small>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <Icon icon="mdi:inbox-outline" style={{ fontSize: '3rem' }} className="text-muted mb-3" />
                                                    <h6 className="text-muted">Belum ada riwayat transaksi</h6>
                                                    <p className="text-muted small">Transaksi yang telah diselesaikan akan muncul di sini</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer border-0">
                                <div className="d-flex gap-2 w-100">
                                    {currentMitra.mitra_data?.nomor_wa && (
                                        <a 
                                            href={`https://wa.me/${currentMitra.mitra_data.nomor_wa.replace(/[^0-9]/g, '')}`}
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="btn btn-success flex-fill"
                                        >
                                            <Icon icon="mdi:whatsapp" className="me-2" />
                                            Hubungi via WhatsApp
                                        </a>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-secondary flex-fill"
                                        onClick={() => setCurrentMitra(null)}
                                    >
                                        <Icon icon="mdi:close" className="me-2" />
                                        Tutup
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MitraCompletedDashboard;